import { useEffect, useRef, useCallback } from "react";
import type { NonUserAppFeatures } from "@glide/app-description";
import { useWireFrontendActionCallbacks } from "./use-wire-frontend-action-callbacks";

interface UseInactivityLogoutProps {
    readonly features: NonUserAppFeatures | undefined;
    readonly isAuthenticated: boolean;
}

export function useInactivityLogout({ features, isAuthenticated }: UseInactivityLogoutProps): void {
    const { signOut } = useWireFrontendActionCallbacks();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<number>(Date.now());

    const logoutConfig = features?.automaticallyLogoutInactiveUsers ?? false;
    const shouldBeEnabled = logoutConfig !== false && isAuthenticated;
    const timeoutMinutes = typeof logoutConfig === "number" ? logoutConfig : 100_000; // This should never happen
    const timeoutMs = timeoutMinutes * 60 * 1000;

    const resetTimer = useCallback(() => {
        const now = Date.now();
        lastActivityRef.current = now;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (shouldBeEnabled) {
            timeoutRef.current = setTimeout(() => {
                signOut();
            }, timeoutMs);
        }
    }, [shouldBeEnabled, signOut, timeoutMs]);

    useEffect(() => {
        if (!shouldBeEnabled) {
            return;
        }

        const activityEvents = [
            "mousedown",
            "mousemove",
            "keypress",
            "keydown",
            "scroll",
            "touchstart",
            "touchmove",
            "click",
        ];

        activityEvents.forEach(event => {
            window.addEventListener(event, resetTimer, { passive: true });
        });

        resetTimer();

        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [shouldBeEnabled, resetTimer]);
}
